import React, { useMemo, useState } from 'react';
import { decompressData } from 'utils/common';
import AIEvalProduct from './searchComponents/AIEvalProduct';
import ProductScreeningResultsTable from './tables/commodityTable/ProductScreeningResultsTable';

interface CustomsFlagsSummaryProps {
  hsCode?: string;
  aiSums?: any[];
  className?: string;
}

interface CustomsFlagsDetailsProps extends CustomsFlagsSummaryProps {
  workflowResults?: any;
}

const getRiskClass = (riskLevel: string) => {
  if (riskLevel === 'Low') return 'low';
  if (riskLevel === 'Medium') return 'medium';
  return 'high';
};

const CustomsFlagsSummary: React.FC<CustomsFlagsSummaryProps> = ({
  hsCode,
  aiSums = [],
  className = '',
}) => {
  const riskSummaries = Array.isArray(aiSums)
    ? aiSums.filter((summary) => summary?.risk_level)
    : [];

  return (
    <div
      className={`classify-res-comp-body customs-flags-summary ${className}`.trim()}
    >
      <div className='classify-res-comp-body-cont'>
        <div className='classify-res-comp-body-cont-sec'>
          <div className='classify-res-comp-body-cont-sec-txt'>HS Code:</div>
          {riskSummaries.map((summary, index) => {
            const riskLevel = String(summary.risk_level);

            return (
              <div
                key={`${riskLevel}-${index}`}
                className={`classify-res-comp-body-cont-sec-icon ${getRiskClass(
                  riskLevel,
                )}`}
              >
                <div className='classify-res-comp-body-cont-sec-icon-txt'>
                  {riskLevel === 'Low' || riskLevel === 'Medium'
                    ? riskLevel
                    : 'High'}
                </div>
              </div>
            );
          })}
        </div>
        <div className='classify-res-comp-body-cont-txt'>{hsCode}</div>
      </div>
    </div>
  );
};

const normalizeStoredWorkflowResults = (workflowResults: any) => {
  const storedResults = workflowResults?.allResults ?? workflowResults;
  const unpackedResults =
    typeof storedResults === 'string' || Array.isArray(storedResults)
      ? typeof storedResults === 'string' ||
        storedResults.every((item: any) => typeof item === 'string')
        ? decompressData(storedResults)
        : storedResults
      : storedResults;

  if (unpackedResults?.data && Array.isArray(unpackedResults.data)) {
    return { isLoading: false, data: unpackedResults.data };
  }

  const rows = Array.isArray(unpackedResults)
    ? unpackedResults
    : unpackedResults
    ? [unpackedResults]
    : [];

  const normalizedRows = rows
    .map((row: any) => {
      if (row?.result?.body?.hits) return row;
      if (!Array.isArray(row?.docs)) return null;

      return {
        result: {
          body: {
            hits: {
              total: { value: Number(row.hits || row.docs.length) },
              hits: row.docs.map((doc: any) =>
                doc?._source ? doc : { _source: doc },
              ),
            },
          },
        },
      };
    })
    .filter(Boolean);

  return { isLoading: false, data: normalizedRows };
};

const CustomsSectionHeader: React.FC<{
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ title, isExpanded, onToggle }) => (
  <button
    type='button'
    className='standard-workflow-customs-section-toggle'
    onClick={onToggle}
    aria-expanded={isExpanded}
  >
    <span className='standard-workflow-customs-section-icon'>$</span>
    <strong>{title}</strong>
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        d={isExpanded ? 'M4 6L8 10L12 6' : 'M10 12L6 8L10 4'}
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </button>
);

export const CustomsFlagsDetails: React.FC<CustomsFlagsDetailsProps> = ({
  hsCode,
  aiSums = [],
  workflowResults,
}) => {
  const [page, setPage] = useState(0);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const results = useMemo(
    () => normalizeStoredWorkflowResults(workflowResults),
    [workflowResults],
  );

  return (
    <div className='standard-workflow-customs-details'>
      <CustomsFlagsSummary hsCode={hsCode} aiSums={aiSums} />

      <section className='standard-workflow-customs-section'>
        <CustomsSectionHeader
          title='Important Notes'
          isExpanded={notesExpanded}
          onToggle={() => setNotesExpanded((expanded) => !expanded)}
        />
        {notesExpanded && (
          <AIEvalProduct aiSums={aiSums} aiLoading={false} />
        )}
      </section>

      <section className='standard-workflow-customs-section'>
        <CustomsSectionHeader
          title='Screening Results'
          isExpanded={resultsExpanded}
          onToggle={() => setResultsExpanded((expanded) => !expanded)}
        />
        {resultsExpanded && (
          <div className='entity-screening-results-shell product-screening-results-shell'>
            <ProductScreeningResultsTable
              handleSubmit={() => undefined}
              setPage={setPage}
              page={page}
              results={results}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default CustomsFlagsSummary;
